import { createContext, useContext, type RefObject } from 'react';

/**
 * [MCV-1724] Layout's main column: the only scroller in the signed-in shell (the document never
 * scrolls there, and the top bar sits above it, not over it). Null outside Layout.
 */
export const AppScrollRootContext = createContext<RefObject<HTMLDivElement | null> | null>(null);

export const useAppScrollRoot = () => useContext(AppScrollRootContext);

/** The scroll margin an element declares (what `scrollIntoView` honours), or 0. */
export const declaredScrollMargin = (el: Element): number => {
  const margin = parseFloat(getComputedStyle(el).scrollMarginTop);
  return Number.isFinite(margin) ? margin : 0;
};

/**
 * Scrolls `root` so `target` sits at its own scroll-margin-top below the root's top edge, and moves
 * nothing else.
 *
 * This replaces `target.scrollIntoView()` inside the app shell. `scrollIntoView` scrolls EVERY
 * scrollable ancestor, not only the column. The signed-in shell gives the document no scroll range
 * (the impersonation banner is a row of SignedInShell, not a body margin [MCV-1752]), but setting
 * the column's own `scrollTop` cannot touch an ancestor even if a page overflows the shell.
 * (Layout's column has no border or scroll-padding, so its bounding-rect top is the edge
 * `scrollIntoView` would align to.)
 */
export const scrollTargetIntoRoot = (root: HTMLElement, target: Element) => {
  root.scrollTop +=
    target.getBoundingClientRect().top -
    root.getBoundingClientRect().top -
    declaredScrollMargin(target);
};
